/**
 * Common room sizes for each space type — the tiles on the size step of the
 * Advanced mode create wizard (designer/planner/CreateRoomWizard). Feet:
 * `length` runs along the front wall, `width` front to back, `height` the walls.
 *
 * Starting points only: every one stays editable on the blueprint. A space
 * type with no list here gets `other`'s.
 */

export interface RoomSizePreset {
  id: string;
  name: string;
  length: number;
  width: number;
  height: number;
}

const p = (id: string, name: string, length: number, width: number, height: number): RoomSizePreset => ({ id, name, length, width, height });

export const ROOM_SIZE_PRESETS: Record<string, RoomSizePreset[]> = {
  garage: [
    p('garage-1', 'Single car', 12, 22, 9),
    p('garage-2', 'Double car', 20, 22, 9),
    p('garage-2xl', 'Large double', 24, 24, 10),
    p('garage-3', 'Triple car', 32, 24, 10),
  ],
  'living-space': [
    p('living-s', 'Bedroom', 10, 12, 8),
    p('living-m', 'Family room', 14, 16, 8),
    p('living-l', 'Living room', 16, 20, 9),
    p('living-xl', 'Great room', 20, 24, 10),
  ],
  basement: [
    p('basement-s', 'Small rec room', 12, 16, 8),
    p('basement-m', 'Rec room', 16, 24, 8),
    p('basement-l', 'Large basement', 24, 32, 8),
    p('basement-xl', 'Full basement', 28, 40, 8),
  ],
  'laundry-room': [
    p('laundry-s', 'Compact', 6, 8, 8),
    p('laundry-m', 'Standard', 8, 10, 8),
    p('laundry-l', 'Laundry + mudroom', 10, 12, 8),
    p('laundry-xl', 'Large utility', 12, 14, 9),
  ],
  workshop: [
    p('workshop-s', 'Small shop', 12, 16, 9),
    p('workshop-m', 'Workshop', 16, 24, 10),
    p('workshop-l', 'Large shop', 24, 30, 10),
    p('workshop-xl', 'Pole barn', 30, 40, 12),
  ],
  shed: [
    p('shed-s', 'Garden shed', 8, 10, 8),
    p('shed-m', 'Storage shed', 10, 12, 8),
    p('shed-l', 'Large shed', 12, 16, 9),
    p('shed-xl', 'Backyard studio', 12, 20, 10),
  ],
  washroom: [
    p('washroom-s', 'Half bath', 5, 6, 8),
    p('washroom-m', 'Full bath', 8, 10, 8),
    p('washroom-l', 'Primary bath', 10, 14, 9),
    p('washroom-xl', 'Public washroom', 14, 18, 9),
  ],
  'commercial-kitchen': [
    p('kitchen-s', 'Small kitchen', 12, 16, 10),
    p('kitchen-m', 'Restaurant kitchen', 20, 24, 10),
    p('kitchen-l', 'Large kitchen', 24, 32, 12),
    p('kitchen-xl', 'Production kitchen', 30, 40, 12),
  ],
  other: [
    p('other-s', 'Small', 10, 10, 8),
    p('other-m', 'Medium', 12, 16, 9),
    p('other-l', 'Large', 20, 20, 10),
    p('other-xl', 'Extra large', 24, 30, 10),
  ],
};

export function roomSizePresets(spaceType: string): RoomSizePreset[] {
  return ROOM_SIZE_PRESETS[spaceType] ?? ROOM_SIZE_PRESETS.other;
}

/** The size a space starts on: its second preset (the everyday one), or the first. */
export function defaultRoomSize(spaceType: string): RoomSizePreset {
  const list = roomSizePresets(spaceType);
  return list[1] ?? list[0];
}
